// Modules
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';

import Person from 'services/data/person';
import PersonLink from 'components/models/person/person-link';
import PhoneNumber from 'services/data/phone-number';
import SVG from 'components/widgets/icons/svg';
import Tooltip from 'components/widgets/overlays/tooltip';

// Hooks
import { useTriageMutation } from 'modules/triage/hooks/useTriageMutation';

import { Answer, CallDetails, Claim, Transfer } from './triage-options';
import TransferForm from './triage-options/forms/transfer';
import TriageItem from './triage-item';


export default function CallItem({ item: call, currentUser, onDashboard, ...passed }) {
  const { update, claim, answer } = useTriageMutation({ type: 'call', currentUser });

  const { open } = call;
  const companyNickname = !_.isEmpty(call.company) && call.company && call.company.nickname;
  const person = !_.isEmpty(call.fromPerson) && call.fromPerson;
  const answerable = _.includes(['Incoming', 'Failed Transfer'], call.callStatus);
  const claimed = !_.isEmpty(call.claimedBy);
  const claimable = call.callStatus !== 'Incoming' && !claimed;
  const transferrable = call.callStatus === 'Active' && call.userOnCall.id === currentUser.id;
  const needsAction = answerable || claimable || transferrable;
  const transferringTo = !_.isEmpty(call.transferringTo) && Person.fullName(call.transferringTo);
  const lastUserOnCall = !_.isEmpty(call.lastUserOnCall) && Person.fullName(call.lastUserOnCall);
  const userOnCall = !_.isEmpty(call.userOnCall) && Person.fullName(call.userOnCall);
  const answerFailure = `Answer by ${transferringTo} failed`;
  const transferFailure = `Transfer from ${lastUserOnCall} to ${transferringTo} failed`;
  const initialAnswer = call.callStatus === 'Transferring' && !userOnCall;
  const callStatus = initialAnswer ? 'Answering' : call.callStatus;
  const matchedFrom = call.matchedFrom;

  const statusMap = {
    Answering: `Answering: ${transferringTo}`,
    Transferring: `Transferring to: ${transferringTo}`,
    Active: `Active: ${userOnCall}`,
    'Failed Transfer': lastUserOnCall ? transferFailure : answerFailure
  };

  const origin = (
    <Tooltip className="meta-tooltip" placement="bottom" title={PhoneNumber.format(call.from)}>
      <span className="truncate">
        { matchedFrom && person && !onDashboard && <PersonLink person={person} /> }
        { matchedFrom && person && onDashboard && Person.fullName(person) }
        { matchedFrom && !person && <span>{companyNickname}</span> }
        { !matchedFrom && <span>{PhoneNumber.format(call.from)}</span> }
      </span>
      { !onDashboard && <SVG src="svg/info.svg" /> }
    </Tooltip>
  );

  return (
    <TriageItem
      {...passed}
      itemOrigin={origin}
      payloadOrStatus={statusMap[callStatus] || call.callStatus}
      currentUser={currentUser}
      updateTriageItem={update}
      triageItem={call}
      onDashboard={onDashboard}
      className={classNames(_.kebabCase(call.callStatus))}
    >
      { !_.isEmpty(call.recording) && !open && <CallDetails call={call} /> }

      { needsAction && !open &&
        <div className="triage-primary-options">
          { answerable && <Answer call={call} answer={answer} /> }
          { claimable && <Claim triageItem={call} claim={claim} /> }
          { transferrable && <Transfer call={call} /> }
        </div>
      }

      { open === 'transfer' &&
        <TransferForm call={call} />
      }
    </TriageItem>
  );
}

CallItem.propTypes = {
  item: PropTypes.shape({
    open: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    setOpen: PropTypes.func
  }),
  currentUser: PropTypes.object.isRequired,
  onDashboard: PropTypes.bool
};
